<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import TextHeadline from '../atoms/app/text/TextHeadline.vue';

defineProps({
    initialFocus: {
        type: Object,
    },
    panelClasses: {
        type: String,
        default: '',
    },
    containsIframe: {
        type: Boolean,
        default: false,
    },
});

const open = defineModel('open', { type: Boolean });
const emit = defineEmits(['after-leave']);

function afterLeave() {
    open.value = false;
    emit('after-leave');
}

</script>

<template>
    <TransitionRoot
        :show="open"
        as="template"
        @after-leave="afterLeave"
    >
        <Dialog
            :initial-focus
            @close="open = false"
        >
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-gray-900-40 z-[700]"
                    aria-hidden="true"
                />
            </TransitionChild>
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0 -translate-y-2xl"
                enter-to="opacity-100 scale-100 translate-y-0"
                leave="duration-200 ease-in"
                leave-from="opacity-100 translate-y-0"
                leave-to="opacity-0 -translate-y-4xl"
            >
                <div class="fixed inset-0 flex w-screen items-center justify-center p-l z-[800] max-h-full">
                    <DialogPanel
                        class="relative w-full rounded
                               bg-white inset-0 z-[800] max-h-full overflow-y-auto"
                        :class="[
                            panelClasses,
                            {
                                'h-full overflow-y-hidden max-w-[880px] pt-l px-0 pb-0': containsIframe,
                                'max-w-[550px] p-3xl': !containsIframe,
                            },
                        ]"
                    >
                        <button
                            class="leading-[0] absolute top-l right-l w-l h-l text-gray-600 cursor-pointer"
                            data-qa="close button"
                            type="button"
                            aria-label="Dialog schließen"
                            @click="open = false"
                        >
                            <AppIcon name="cross"/>
                        </button>
                        <DialogTitle>
                            <TextHeadline
                                class="text-center mb-l"
                                :class="containsIframe && 'mb-xl'"
                                variant="md-700"
                            >
                                <slot name="headline"/>
                            </TextHeadline>
                        </DialogTitle>
                        <p
                            class="text-center"
                            :class="containsIframe && 'h-full'"
                        >
                            <slot/>
                        </p>
                        <div
                            v-if="$hasSlotContent($slots.actions)"
                            class="mt-l text-center"
                        >
                            <slot
                                name="actions"
                            />
                        </div>
                    </DialogPanel>
                </div>
            </TransitionChild>
        </Dialog>
    </TransitionRoot>
</template>

<style lang="scss" scoped>
.bg-gray-900-40 {
    background-color: rgba(23, 25, 28, 0.4);
}
</style>
